import React, { useMemo } from "react";
import {
  Box,
  Card,
  Grid,
  Skeleton,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  LinearProgress,
} from "@material-ui/core";
import { Business, Timer, Assessment, Assignment } from "@material-ui/icons";
import { fNumber, fPercent } from "src/utils/formatNumber";
import Chart from "react-apexcharts";
import useChart from "../charts/use-chart";

export default function TiempoPorClienteEmpleado({ data, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Procesar datos para el gráfico de barras horizontales
  const barData = useMemo(() => {
    if (!data?.topClientes?.length) return { categories: [], series: [] };

    // Limitar nombres largos
    const truncateName = (name, maxLength = 25) => {
      return name.length > maxLength
        ? `${name.substring(0, maxLength)}...`
        : name;
    };

    return {
      categories: data.topClientes.map((item) =>
        truncateName(item.nombreCliente, isMobile ? 20 : 30),
      ),
      diasTotales: data.topClientes.map((item) => item.diasTotales),
      porcentajes: data.topClientes.map((item) => item.porcentajeTiempo),
    };
  }, [data?.topClientes, isMobile]);

  // Configuración del gráfico de barras horizontales
  const barOptions = useChart({
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        barHeight: isMobile ? "75%" : "65%",
        distributed: true,
      },
    },
    colors: [
      "#2196f3",
      "#4caf50",
      "#ff9800",
      "#f44336",
      "#9c27b0",
      "#00bcd4",
      "#ff5722",
      "#607d8b",
      "#3f51b5",
      "#8bc34a",
    ],
    xaxis: {
      categories: barData.categories,
      labels: {
        style: {
          fontSize: isMobile ? "10px" : "11px",
        },
        formatter: (value) => `${fNumber(value)} días`,
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: isMobile ? "9px" : "11px",
        },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: isMobile ? "9px" : "10px",
        fontWeight: "bold",
      },
      formatter: (value) => `${fNumber(value)}d`,
    },
    tooltip: {
      y: {
        formatter: (value, { dataPointIndex }) => {
          const porcentaje = barData.porcentajes[dataPointIndex];
          return `${fNumber(value)} días (${fPercent(porcentaje)})`;
        },
      },
    },
    legend: {
      show: false,
    },
    grid: {
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
  });

  const barSeries = [
    {
      name: "Días Dedicados",
      data: barData.diasTotales,
    },
  ];

  // Función para obtener color según porcentaje
  const getColorByPercentage = (percentage) => {
    if (percentage >= 30) return "#f44336"; // Alto: rojo
    if (percentage >= 20) return "#ff9800"; // Medio: naranja
    if (percentage >= 10) return "#2196f3"; // Moderado: azul
    return "#4caf50"; // Bajo: verde
  };

  if (loading) {
    return (
      <Card sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Card>
    );
  }

  const { topClientes = [], resumen = {} } = data || {};
  const {
    totalDias = 0,
    totalClientes = 0,
    totalTareasCompletadas = 0,
    clientesTop10Porcentaje = 0,
  } = resumen;

  return (
    <Card sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold">
          Distribución de Tiempo por Cliente
        </Typography>
        <Chip
          icon={<Business sx={{ fontSize: isMobile ? 16 : 18 }} />}
          label={`${totalClientes} clientes`}
          size="small"
          variant="outlined"
          sx={{
            borderColor: "primary.main",
            color: "primary.main",
            fontWeight: "medium",
          }}
        />
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        {/* Columna izquierda - Resumen y Métricas */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            {/* Métrica principal - Total de días */}
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                bgcolor: "primary.lighter",
                borderRadius: 2,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={1}
              >
                <Timer
                  sx={{
                    color: "primary.main",
                    mr: 1,
                    fontSize: isMobile ? 20 : 24,
                  }}
                />
                <Typography
                  variant={isMobile ? "body2" : "subtitle2"}
                  color="primary.main"
                  fontWeight="bold"
                >
                  Tiempo Total
                </Typography>
              </Box>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                color="primary.main"
                fontWeight="bold"
              >
                {fNumber(totalDias)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                días dedicados
              </Typography>
            </Box>

            {/* Concentración del Top 10 */}
            {clientesTop10Porcentaje > 0 && (
              <Box
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "warning.main",
                  borderRadius: 2,
                  bgcolor: "warning.lighter",
                }}
              >
                <Box display="flex" alignItems="center" mb={1}>
                  <Assessment
                    sx={{
                      color: "warning.main",
                      mr: 1,
                      fontSize: isMobile ? 18 : 20,
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="warning.main"
                    fontWeight="bold"
                  >
                    Concentración
                  </Typography>
                </Box>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  color="warning.main"
                  fontWeight="bold"
                  textAlign="center"
                >
                  {fPercent(clientesTop10Porcentaje)}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  textAlign="center"
                  mt={0.5}
                >
                  del tiempo en Top 10
                </Typography>
              </Box>
            )}

            {/* Tareas completadas */}
            {totalTareasCompletadas > 0 && (
              <Box
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "success.main",
                  borderRadius: 2,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box display="flex" alignItems="center">
                    <Assignment
                      sx={{
                        color: "success.main",
                        mr: 1,
                        fontSize: isMobile ? 18 : 20,
                      }}
                    />
                    <Typography variant="caption" fontWeight="bold">
                      Tareas Completadas
                    </Typography>
                  </Box>
                  <Chip
                    label={fNumber(totalTareasCompletadas)}
                    size="small"
                    sx={{
                      bgcolor: "success.main",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Lista de top 3 clientes */}
            {topClientes.length > 0 && (
              <Box
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  fontWeight="bold"
                  gutterBottom
                  display="block"
                >
                  Top 3 Clientes
                </Typography>
                <Stack spacing={1.5} mt={1}>
                  {topClientes.slice(0, 3).map((cliente, index) => (
                    <Box key={cliente.rutCliente}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={0.5}
                      >
                        <Box display="flex" alignItems="center" flex={1}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              bgcolor:
                                index === 0
                                  ? "#FFD700"
                                  : index === 1
                                    ? "#C0C0C0"
                                    : "#CD7F32",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 1,
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              sx={{ color: "white", fontSize: 10 }}
                            >
                              {index + 1}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {cliente.nombreCliente.length > 20
                              ? `${cliente.nombreCliente.substring(0, 20)}...`
                              : cliente.nombreCliente}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color="primary.main"
                          sx={{ ml: 1, flexShrink: 0 }}
                        >
                          {fNumber(cliente.diasTotales)}d
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(cliente.porcentajeTiempo, 100)}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: "grey.200",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: getColorByPercentage(
                              cliente.porcentajeTiempo,
                            ),
                            borderRadius: 2,
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                        textAlign="right"
                        mt={0.3}
                      >
                        {fPercent(cliente.porcentajeTiempo)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Grid>

        {/* Columna derecha - Gráfico de barras */}
        <Grid item xs={12} lg={8}>
          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              color="textSecondary"
              fontWeight="medium"
            >
              Top 10 Clientes por Tiempo Dedicado
            </Typography>
            {barData.categories.length > 0 ? (
              <Box sx={{ height: { xs: 400, sm: 450 } }}>
                <Chart
                  options={barOptions}
                  series={barSeries}
                  type="bar"
                  height="100%"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "background.neutral",
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  No hay datos de clientes en el período seleccionado
                </Typography>
              </Box>
            )}

            {/* Nota explicativa */}
            {totalClientes > 10 && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: "info.lighter",
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" color="info.dark">
                  <strong>💡 Info:</strong> Se muestran los 10 clientes
                  principales de un total de {totalClientes} clientes atendidos
                  en el período.
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
}
